import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { Drawer, Button, Box, IconButton, Grid, Typography, Autocomplete, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InputField from "../../components/InputField";
import { t } from "i18next";
import styles from "./styles";
import CustomSelect from "../../components/SelectField";
import { Status } from "./types";
import { useEffect, useState } from "react";
import { IUser } from "../AddUser/types";
import { getApiErrorMessage } from "../../utils/commonHelpers";
import getAPIUrl from "../../config";
import { getApi } from "../../utils/apis";
import { ILocation } from "../Locations/types";
import { useTranslation } from "react-i18next";

type FilterInputs = {
    locationId: string;
    userId: string;
    buildingName: string;
    floorNumber: string;
    identificationNumber: string;
    status: string;
};

type FilterDrawerProps = {
    drawerOpen: boolean;
    toggleDrawer: (open: boolean) => () => void;
    handleApplyFilters: (filters: FilterInputs) => void;
};

const defaultValues = {
    locationId: "",
    userId: "",
    buildingName: "",
    floorNumber: "",
    identificationNumber: "",
    status: ""
};


const statusList = Object.entries(Status).map((rec) => ({
    name: rec[0],
    value: rec[1],
}));



const FilterDrawer = ({ drawerOpen, toggleDrawer, handleApplyFilters }: FilterDrawerProps) => {
    const [clientId, setClientId] = useState<string>("");
    const [locations, setLocations] = useState<ILocation[]>([]);
    const [users, setUsers] = useState<IUser[]>([]);
    const [errorToast, setErrorToast] = useState<boolean>(false);
    const [toastErrorMsg, setErrorToastMsg] = useState<string>("");
    const { t } = useTranslation();
    const { formState: { errors }, control, handleSubmit, reset } = useForm<FilterInputs>({ defaultValues: defaultValues });


    useEffect(() => {

        const getLocations = async (clientId?: string) => {
            try {
                const locationsData = clientId
                    ? await getApi(
                        `${getAPIUrl()}/api/v1/locations/name?clientId=${clientId}`
                    )
                    : await getApi(`${getAPIUrl()}/api/v1/locations/name`);
                setLocations(locationsData.data);
            } catch (error) {
                if (error instanceof Error) {
                    const errorMsg = getApiErrorMessage(error);
                    setErrorToast(true);
                    setErrorToastMsg(errorMsg);
                }
            }
        };
        // const getUsers = async () => {
        //     try {
        //         const usersData = await getApi(
        //             `${getAPIUrl()}/api/v1/users/name?roleName=Picker`
        //         );
        //         setUsers(usersData.data);
        //     } catch (error) {
        //         if (error instanceof Error) {
        //             const errorMsg = getApiErrorMessage(error);
        //             setErrorToast(true);
        //             setErrorToastMsg(errorMsg);
        //         }
        //     }
        // };
        const getUsers = async () => {
            try {
                const pickupLocations = await getApi(
                    `${getAPIUrl()}/api/v1/pickup-locations`
                );
                const users = pickupLocations.data.map((location: any) => location.user);
                setUsers(users);
            } catch (error) {
                if (error instanceof Error) {
                    const errorMsg = getApiErrorMessage(error);
                    setErrorToast(true);
                    setErrorToastMsg(errorMsg);
                }
            }
        };
        getLocations(clientId);
        getUsers();
    }, [clientId]);


    const onSubmit: SubmitHandler<FilterInputs> = (data) => {
        handleApplyFilters(data);
        toggleDrawer(false)();
    };

    const handleClear = () => {
        reset(); // Clear the form fields
        handleApplyFilters({
            locationId: "", userId: "", buildingName: "",
            floorNumber: "",
            identificationNumber: "", status: ""
        });
        // Clear filters
        // toggleDrawer(false)();
    };

    const locationsList =
        locations &&
        locations.map((location) => ({ name: location.name, value: location.id }));

    const usersList =
        users &&
        users.map((user) => ({
            value: user.id,
            name: `${user.firstName} ${user.lastName}`,
        }));



    return (
        <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)} sx={styles.filterDrawer}  >

            <Box component="form" sx={{ ...styles.formContainer, paddingLeft: "1rem", paddingRight: "1rem" }} onSubmit={handleSubmit(onSubmit)}>
                <Typography sx={styles.subHeading}>{t("pickers")}</Typography>
                <IconButton
                    onClick={toggleDrawer(false)}
                    sx={{ position: "absolute", top: 8, right: 8 }}
                >
                    <CloseIcon />
                </IconButton>
                <Grid container spacing={2} xs={14} >

                    <Grid item xs={6}>
                        <Controller
                            name="locationId"
                            control={control}
                            render={({ field }) => (
                                <CustomSelect
                                    {...field}
                                    id="locationId"
                                    onChange={(e) => field.onChange(e.target.value)}
                                    size="small"
                                    fullWidth
                                    placeholder="Location Name"
                                    items={locationsList}
                                    error={!!errors.locationId?.message}
                                    errorMessage={errors.locationId?.message}
                                    label="Location Name"
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            name="userId"
                            control={control}
                            render={({ field }) => (
                                <CustomSelect
                                    {...field}
                                    id="userId"
                                    onChange={(e) => field.onChange(e.target.value)}
                                    size="small"
                                    fullWidth
                                    placeholder="Picker"
                                    items={usersList}
                                    error={!!errors.userId?.message}
                                    errorMessage={errors.userId?.message}
                                    label="Picker"
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="buildingName"
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    id="buildingName"
                                    label={t("buildingName")}
                                    placeholder={t("buildingName")}
                                    type="text"
                                    showCross
                                    fullWidth
                                    size="small"
                                    handleCrossClick={() => field.onChange("")}
                                    error={!!errors.buildingName?.message}
                                    errorMessage={errors.buildingName?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            name="floorNumber"
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    id="floorNumber"
                                    label={t("floorNumber")}
                                    placeholder={t("floorNumber")}
                                    type="text"
                                    showCross
                                    fullWidth
                                    size="small"
                                    handleCrossClick={() => field.onChange("")}
                                    error={!!errors.floorNumber?.message}
                                    errorMessage={errors.floorNumber?.message}
                                />
                            )}
                        />

                    </Grid>
                    <Grid item xs={6}>
                        <Controller
                            name="identificationNumber"
                            control={control}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    id="identificationNumber"
                                    label={t("identificationNumber")}
                                    placeholder={t("identificationNumber")}
                                    type="text"
                                    showCross
                                    fullWidth
                                    size="small"
                                    handleCrossClick={() => field.onChange("")}
                                    error={!!errors.identificationNumber?.message}
                                    errorMessage={errors.identificationNumber?.message}
                                />
                            )}
                        />

                    </Grid>

                    <Grid item xs={6}>
                        <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                                <CustomSelect
                                    {...field}
                                    id="status"
                                    onChange={(e) => field.onChange(e.target.value)}
                                    size="small"
                                    fullWidth
                                    placeholder="Status"
                                    items={statusList}
                                    error={!!errors.status?.message}
                                    errorMessage={errors.status?.message}
                                    label="Status"
                                />

                            )}
                        />

                    </Grid>
                </Grid>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "8px",
                        marginTop: "16px",
                        paddingBottom: "3rem",
                    }}
                >
                    <Button
                        variant="outlined"
                        color="secondary"
                        fullWidth
                        onClick={handleClear}
                    >
                        {t("clear")}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                    >
                          {t("filter")}
                    </Button>
                </Box>

            </Box>

        </Drawer>
    );
};

export default FilterDrawer;
